import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiChartTimelineVariant, mdiCheck, mdiClose, mdiFilterVariant, mdiMagnify } from '@mdi/js'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import { AssetPicker } from './AssetPicker'
import { useDebounce } from '../hooks/useDebounce'
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from './ui/popover'
import { useAllContracts, useAssetsByUids, useContractTypes } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { buildChains, buildRows, parseDate, type Chain, type TimelineAsset, type TimelineContract, type TimelineRow } from '../lib/contracts-timeline-rows'
import { fetchAllPages, listQueryKey } from '../api/paginateAll'
import { useQuery } from '@tanstack/react-query'

// Statuses selectable in the timeline filter (draft contracts are never shown —
// they are not in force yet, so their notice window is meaningless).
const STATUS_OPTIONS = ['open', 'in_progress', 'closed'] as const

// One day in milliseconds, used to pad the time domain so edge bars aren't
// flush against the track border.
const DAY_MS = 24 * 60 * 60 * 1000

// How long before the last day to denounce a contract starts reading as
// urgent. The reporter's figure (#1352): under three months, the notice
// window is close enough that the decision has to be taken now.
const NOTICE_SOON_MS = 92 * DAY_MS

/** How a contract period reads on the axis today (#1352). */
type PeriodState = 'ended' | 'notice_soon' | 'active'

/**
 * periodState places one period against the current date: a period whose end
 * is behind us is over and greys out, a live one whose last day to denounce
 * falls inside the next three months is urgent, everything else in force is
 * ordinary. A period that has not started yet reads as active — it is a
 * commitment, not history.
 */
function periodState(m: TimelineContract, now: number): PeriodState {
  const end = parseDate(m.end_date)
  if (end !== null && end < now) return 'ended'

  const term = parseDate(m.termination_date)
  if (term !== null && term >= now && term - now <= NOTICE_SOON_MS) return 'notice_soon'

  return 'active'
}

/** Bar classes per state. Bounded bars only — an unbounded one keeps its own
 * dashed treatment, which says "we do not know the dates" rather than "this is
 * over". */
const PERIOD_CLASSES: Record<PeriodState, { head: string; member: string }> = {
  ended: { head: 'bg-muted-foreground/45', member: 'bg-muted-foreground/30' },
  notice_soon: { head: 'bg-warning', member: 'bg-warning/60' },
  active: { head: 'bg-primary/80', member: 'bg-primary/45' },
}

// Width of the contract label column, kept per viewer.
const LABEL_WIDTH_KEY = 'contracts-timeline-label-width'
const LABEL_WIDTH_DEFAULT = 288
const LABEL_WIDTH_MIN = 160
const LABEL_WIDTH_MAX = 640
const LABEL_WIDTH_STEP = 16

export function clampLabelWidth(w: number): number {
  if (!Number.isFinite(w)) return LABEL_WIDTH_DEFAULT
  return Math.min(LABEL_WIDTH_MAX, Math.max(LABEL_WIDTH_MIN, Math.round(w)))
}

function readLabelWidth(): number {
  try {
    const raw = window.localStorage.getItem(LABEL_WIDTH_KEY)
    if (raw === null) return LABEL_WIDTH_DEFAULT
    return clampLabelWidth(Number(raw))
  } catch {
    return LABEL_WIDTH_DEFAULT
  }
}

function writeLabelWidth(w: number | null) {
  try {
    if (w === null) window.localStorage.removeItem(LABEL_WIDTH_KEY)
    else window.localStorage.setItem(LABEL_WIDTH_KEY, String(w))
  } catch {
    // Storage blocked: the width just doesn't persist.
  }
}

export function ContractsTimeline() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { contract_type_uid?: string; status?: string; q?: string; asset_uid?: string }
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()

  useLiveSubscription({ entity: 'contracts' })

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/contracts` },
    { title: t('timeline.title') },
  ], [org, t])
  useBreadcrumbs(breadcrumbs)

  // Free-text search: the field is local state so typing is instant, and the
  // URL (and the request) follow after a pause, replacing the history entry.
  const [searchText, setSearchText] = useState(search.q || '')
  const debouncedSearch = useDebounce(searchText, 300)
  useEffect(() => {
    const next = debouncedSearch.trim() || undefined
    if (next === search.q) return
    navigate({
      to: '/$org/contracts/timeline',
      params: { org: org || 'demo' },
      search: (prev: any) => ({ ...prev, q: next }),
      replace: true,
    })
  }, [debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  const [pickedAsset, setPickedAsset] = useState<{ uid: string; name: string } | null>(null)
  const { data: filterAssets = [] } = useAssetsByUids(org || 'demo', search.asset_uid ? [search.asset_uid] : [])
  const assetFilterLabel = (pickedAsset?.uid === search.asset_uid ? pickedAsset?.name : undefined)
    || filterAssets.find(a => a.uid === search.asset_uid)?.name || ''

  const [labelWidth, setLabelWidth] = useState<number>(readLabelWidth)
  const changeLabelWidth = useCallback((w: number) => {
    const next = clampLabelWidth(w)
    setLabelWidth(next)
    writeLabelWidth(next)
  }, [])
  const resetLabelWidth = useCallback(() => {
    setLabelWidth(LABEL_WIDTH_DEFAULT)
    writeLabelWidth(null)
  }, [])
  const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = labelWidth
    const onMove = (ev: PointerEvent) => changeLabelWidth(startW + ev.clientX - startX)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }
  const onResizeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); changeLabelWidth(labelWidth - LABEL_WIDTH_STEP) }
    if (e.key === 'ArrowRight') { e.preventDefault(); changeLabelWidth(labelWidth + LABEL_WIDTH_STEP) }
  }

  const { data: contracts = [], isLoading, error } = useAllContracts(org || 'demo', {
    // Real contracts only — lightweight types are contacts, not commercial contracts.
    lightweight: false,
    contractTypeUid: search.contract_type_uid,
    status: search.status,
    search: search.q,
    assetUid: search.asset_uid,
    assetIncludeChildren: true,
    include: ['contract_type', 'assets'],
  })

  const { data: contractTypes = [] } = useContractTypes(org || 'demo')

  const chains = useMemo<Chain[]>(() => buildChains(contracts as TimelineContract[]), [contracts])

  // The whole asset tree, only to order the rows site, building, lot. Rows
  // fall back to the name while it loads or if it fails.
  const assetsPath = '/api/v1/organizations/{orgCode}/assets'
  const assetsParams = { path: { orgCode: org || 'demo' }, query: {} }
  const { data: allAssets = [] } = useQuery({
    queryKey: listQueryKey(assetsPath, assetsParams),
    enabled: chains.length > 0,
    queryFn: () => fetchAllPages<TimelineAsset>(assetsPath, assetsParams),
  })
  const rows = useMemo(() => buildRows(chains, allAssets), [chains, allAssets])

  // Time domain across every dated point of every chain member.
  const domain = useMemo(() => {
    let min = Infinity
    let max = -Infinity
    const consider = (v: number | null) => {
      if (v === null) return
      if (v < min) min = v
      if (v > max) max = v
    }
    chains.forEach(({ members }) => {
      members.forEach(m => {
        consider(parseDate(m.start_date))
        consider(parseDate(m.end_date))
        consider(parseDate(m.termination_date))
      })
    })
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      // No dated contract at all — fall back to a one-year window around now so
      // unbounded bars still render.
      const now = Date.now()
      return { min: now - 182 * DAY_MS, max: now + 183 * DAY_MS }
    }
    if (min === max) {
      return { min: min - 182 * DAY_MS, max: max + 183 * DAY_MS }
    }
    const pad = (max - min) * 0.03
    return { min: min - pad, max: max + pad }
  }, [chains])

  const span = domain.max - domain.min
  const frac = (v: number) => Math.min(1, Math.max(0, (v - domain.min) / span))
  const pct = (v: number) => `${(frac(v) * 100).toFixed(3)}%`

  // The current date, as a position on the axis. Read once per render rather
  // than per lane so every marker sits on the same pixel.
  const today = Date.now()
  const todayInDomain = today >= domain.min && today <= domain.max

  // Year ticks across the domain for orientation.
  const ticks = useMemo(() => {
    const startYear = new Date(domain.min).getFullYear()
    const endYear = new Date(domain.max).getFullYear()
    const out: { label: string; at: number; left: string }[] = []
    for (let y = startYear; y <= endYear; y++) {
      const t0 = new Date(`${y}-01-01`).getTime()
      if (t0 < domain.min || t0 > domain.max) continue
      out.push({ label: String(y), at: t0, left: pct(t0) })
    }
    return out
  }, [domain]) // eslint-disable-line react-hooks/exhaustive-deps

  // The "today" label is drawn to the RIGHT of its rule, exactly like a year
  // label, so the two pile up whenever today falls just before a year
  // boundary. Measure both rather than guessing a percentage: the axis is
  // flex-1, so any fixed fraction over-suppresses on a wide screen and
  // under-suppresses on a narrow one.
  const todayLabelRef = useRef<HTMLSpanElement>(null)
  const [axisWidth, setAxisWidth] = useState(0)
  const [todayLabelWidth, setTodayLabelWidth] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  // A callback ref, not useRef + useEffect: the axis only mounts once the
  // contracts have loaded, so an effect that runs on mount finds no node and
  // never runs again.
  const attachAxis = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!el) return
    const measure = () => {
      setAxisWidth(el.clientWidth)
      // Child refs attach before the parent's, so the label is already there.
      setTodayLabelWidth(todayLabelRef.current?.offsetWidth ?? 0)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    observerRef.current = ro
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const todayFrac = todayInDomain ? frac(today) : null
  /** A year label the today label would sit on top of. The rule still draws. */
  const tickLabelHidden = (at: number) => {
    if (todayFrac === null || !axisWidth || !todayLabelWidth) return false
    const gap = (frac(at) - todayFrac) * axisWidth
    return gap >= 0 && gap < todayLabelWidth + 8
  }

  const contractLabel = (c: TimelineContract) => c.name || c.code || t('list.untitled')

  const typeName = (uid?: string | null) => {
    const type = contractTypes.find((ct: any) => ct.uid === uid)
    if (!type) return null
    const names = (type as any).names || (type as any).name
    if (!names) return type.code
    if (typeof names === 'string') return names
    return names[locale] || names.en || names.fr || type.code
  }

  const setFilter = (key: 'contract_type_uid' | 'status' | 'q' | 'asset_uid', value?: string) => {
    navigate({
      to: '/$org/contracts/timeline',
      params: { org: org || 'demo' },
      search: (prev: any) => ({ ...prev, [key]: value }),
    })
  }

  const activeTypeName = search.contract_type_uid ? typeName(search.contract_type_uid) : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={mdiChartTimelineVariant}
        title={t('timeline.title')}
        description={t('timeline.subtitle')}
        actions={
          <Link to="/$org/contracts" params={{ org: org || 'demo' }}>
            <Button variant="outline" data-testid="timeline-back-to-list">
              <AppIcon path={SECTION_ICONS.contracts} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t('timeline.back_to_list')}</span>
            </Button>
          </Link>
        }
      />

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder={t('list.search_placeholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-8 pl-10"
            data-testid="timeline-search"
          />
        </div>

        {search.asset_uid ? (
          <Badge variant="secondary" className="gap-1 pr-1 text-xs" data-testid="timeline-filter-asset-badge">
            <span className="max-w-[16rem] truncate">{assetFilterLabel || search.asset_uid}</span>
            <button
              type="button"
              onClick={() => { setPickedAsset(null); setFilter('asset_uid', undefined) }}
              className="rounded p-0.5 hover:bg-muted"
              aria-label={t('timeline.filter_asset_clear')}
              data-testid="timeline-filter-asset-clear"
            >
              <AppIcon path={mdiClose} size="xs" />
            </button>
          </Badge>
        ) : (
          <div className="w-full sm:w-56 [&_input]:h-8">
            <AssetPicker
              org={org || 'demo'}
              testId="timeline-filter-asset"
              placeholder={t('timeline.filter_asset')}
              onChange={(uid, name) => {
                setPickedAsset({ uid, name })
                setFilter('asset_uid', uid)
              }}
            />
          </div>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="timeline-filter-type">
              <AppIcon path={mdiFilterVariant} size="xs" className={`mr-1.5 ${search.contract_type_uid ? 'text-primary' : 'opacity-50'}`} />
              {activeTypeName || t('list.all_contract_types')}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1 max-h-72 overflow-auto">
            <PopoverClose asChild>
              <button type="button" onClick={() => setFilter('contract_type_uid', undefined)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left hover:bg-muted" data-testid="timeline-filter-type-all">
                {t('list.all_contract_types')}
                {!search.contract_type_uid && <AppIcon path={mdiCheck} size="xs" />}
              </button>
            </PopoverClose>
            {contractTypes.map((type: any) => (
              <PopoverClose asChild key={type.uid}>
                <button type="button" onClick={() => setFilter('contract_type_uid', type.uid)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left hover:bg-muted" data-testid={`timeline-filter-type-${type.uid}`}>
                  {typeName(type.uid)}
                  {search.contract_type_uid === type.uid && <AppIcon path={mdiCheck} size="xs" />}
                </button>
              </PopoverClose>
            ))}
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="timeline-filter-status">
              <AppIcon path={mdiFilterVariant} size="xs" className={`mr-1.5 ${search.status ? 'text-primary' : 'opacity-50'}`} />
              {search.status ? t(`status.${search.status}` as any) : t('list.all_statuses')}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-48 p-1">
            <PopoverClose asChild>
              <button type="button" onClick={() => setFilter('status', undefined)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left hover:bg-muted" data-testid="timeline-filter-status-all">
                {t('list.all_statuses')}
                {!search.status && <AppIcon path={mdiCheck} size="xs" />}
              </button>
            </PopoverClose>
            {STATUS_OPTIONS.map(s => (
              <PopoverClose asChild key={s}>
                <button type="button" onClick={() => setFilter('status', s)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left hover:bg-muted" data-testid={`timeline-filter-status-${s}`}>
                  {t(`status.${s}` as any)}
                  {search.status === s && <AppIcon path={mdiCheck} size="xs" />}
                </button>
              </PopoverClose>
            ))}
          </PopoverContent>
        </Popover>

        {/* Legend */}
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground" data-testid="timeline-legend">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm bg-primary/70" aria-hidden="true" />
            {t('timeline.legend_period')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm bg-warning" aria-hidden="true" />
            {t('timeline.legend_notice_soon')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm bg-muted-foreground/45" aria-hidden="true" />
            {t('timeline.legend_ended')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-4 rounded-sm bg-destructive/25 border border-destructive/50" aria-hidden="true" />
            {t('timeline.legend_notice')}
          </span>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64">
              <p className="text-destructive mb-2">{t('list.error')}</p>
              <p className="text-sm text-muted-foreground">{(error as any)?.title || (error as any)?.message || 'Unknown error'}</p>
            </div>
          ) : chains.length === 0 ? (
            <EmptyState
              variant="inline"
              live
              className="h-64 justify-center"
              icon={SECTION_ICONS.contracts}
              title={search.q || search.asset_uid ? t('timeline.empty_filtered') : t('timeline.empty')}
            />
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: labelWidth + 432 }}>
                {/* Axis */}
                <div className="flex items-stretch border-b border-border bg-muted/40">
                  <div
                    className="relative shrink-0 px-4 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider"
                    style={{ width: labelWidth }}
                    data-testid="timeline-label-header"
                  >
                    {t('timeline.column_asset')}
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-valuenow={labelWidth}
                      aria-valuemin={LABEL_WIDTH_MIN}
                      aria-valuemax={LABEL_WIDTH_MAX}
                      aria-label={t('timeline.resize_label_column')}
                      tabIndex={0}
                      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize touch-none hover:bg-primary/40 focus-visible:bg-primary/60 focus-visible:outline-none"
                      onPointerDown={startResize}
                      onKeyDown={onResizeKey}
                      onDoubleClick={resetLabelWidth}
                      data-testid="timeline-label-resize"
                    />
                  </div>
                  <div ref={attachAxis} className="relative flex-1 h-8" data-testid="timeline-axis">
                    {ticks.map(tick => (
                      <div key={tick.label} className="absolute top-0 bottom-0 flex items-center" style={{ left: tick.left }}>
                        <span className="border-l border-border h-full" aria-hidden="true" />
                        {!tickLabelHidden(tick.at) && (
                          <span className="ml-1 text-[11px] text-muted-foreground tabular-nums">{tick.label}</span>
                        )}
                      </div>
                    ))}
                    {/* Today. Without it the axis says when each contract runs
                        but not where we stand in it, which is the one thing a
                        manager reads a timeline for (#1352). */}
                    {todayInDomain && (
                      <div className="absolute top-0 bottom-0 z-10 flex items-center" style={{ left: pct(today) }} data-testid="timeline-today-label">
                        <span className="border-l-2 border-foreground/70 h-full" aria-hidden="true" />
                        <span ref={todayLabelRef} className="ml-1 whitespace-nowrap text-[11px] font-medium text-foreground">{t('timeline.today')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lanes */}
                <div className="divide-y divide-border">
                  {rows.map(row => (
                    <TimelineRowView
                      key={row.assetUid ?? 'no-asset'}
                      org={org || 'demo'}
                      row={row}
                      frac={frac}
                      today={today}
                      todayInDomain={todayInDomain}
                      labelWidth={labelWidth}
                      contractLabel={contractLabel}
                      typeName={typeName}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

interface RowProps {
  org: string
  row: TimelineRow
  frac: (v: number) => number
  /** Now, and whether it falls inside the rendered domain. Passed down rather
   * than read per lane so every lane's marker lands on the same pixel. */
  today: number
  todayInDomain: boolean
  labelWidth: number
  contractLabel: (c: TimelineContract) => string
  typeName: (uid?: string | null) => string | null
}

// One asset: its name on the left, its chains on the right, stacked in as few
// sub-lanes as possible.
function TimelineRowView({ org, row, frac, today, todayInDomain, labelWidth, contractLabel, typeName }: RowProps) {
  const { t } = useTranslation('contracts')
  const testKey = row.assetUid ?? 'none'
  return (
    <div className="flex items-stretch hover:bg-muted/30 py-1" data-testid={`timeline-asset-row-${testKey}`}>
      <div className="shrink-0 px-4 py-2 min-w-0" style={{ width: labelWidth }} data-testid={`timeline-asset-label-${testKey}`}>
        {row.assetUid ? (
          <Link
            to="/$org/assets/$assetUid"
            params={{ org, assetUid: row.assetUid }}
            className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate block"
            title={row.assetName}
          >
            {row.assetName}
          </Link>
        ) : (
          <span className="text-sm font-medium text-muted-foreground truncate block">{t('timeline.no_asset')}</span>
        )}
      </div>
      <div className="flex-1 mr-4 min-w-0">
        {row.lanes.map((lane, i) => (
          <div key={i} className="relative h-8" data-testid={`timeline-sublane-${testKey}-${i}`}>
            <div className="absolute top-1/2 left-0 right-0 h-px bg-border" aria-hidden="true" />
            {lane.map(chain => (
              <TimelineChain
                key={chain.head.uid}
                org={org}
                chain={chain}
                frac={frac}
                today={today}
                todayInDomain={todayInDomain}
                label={contractLabel(chain.head)}
                typeLabel={typeName(chain.head.contract_type_uid)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

interface ChainProps {
  org: string
  chain: Chain
  frac: (v: number) => number
  today: number
  todayInDomain: boolean
  label: string
  typeLabel: string | null
}

function TimelineChain({ org, chain, frac, today, todayInDomain, label, typeLabel }: ChainProps) {
  const { t } = useTranslation('contracts')
  const { head, members } = chain
  const now = today

  const domainMinFrac = 0
  const domainMaxFrac = 1

  // A member's [left, right] fractions, clamping open ends to the domain edges.
  const memberSpan = (m: TimelineContract) => {
    const s = parseDate(m.start_date)
    const e = parseDate(m.end_date)
    const left = s !== null ? frac(s) : domainMinFrac
    const right = e !== null ? frac(e) : domainMaxFrac
    return {
      left,
      right: Math.max(right, left + 0.004),
      openStart: s === null,
      openEnd: e === null,
      unbounded: s === null && e === null,
    }
  }

  const term = parseDate(head.termination_date)
  const headEnd = parseDate(head.end_date)
  // Red band: [termination_date → end_date]; if end is missing it runs to the
  // domain edge (still the too-late zone).
  const bandLeft = term !== null ? frac(term) : null
  const bandRight = term !== null ? (headEnd !== null ? frac(headEnd) : domainMaxFrac) : null
  const chainTitle = [label, typeLabel, members.length > 1 ? t('timeline.chain_count', { count: members.length }) : null]
    .filter(Boolean).join(' · ')

  return (
    <div className="absolute inset-0" data-testid={`timeline-lane-${head.uid}`}>
      {/* Red notice band (drawn under bars so bars stay readable). */}
      {bandLeft !== null && bandRight !== null && (
        <div
          className="absolute top-0 bottom-0 bg-destructive/20 rounded-sm"
          style={{ left: `${(bandLeft * 100).toFixed(3)}%`, width: `${((bandRight - bandLeft) * 100).toFixed(3)}%` }}
          data-testid={`timeline-red-band-${head.uid}`}
          title={t('timeline.notice_window')}
        />
      )}

      {/* Chain member segments. */}
      {members.map((m, i) => {
        const sp = memberSpan(m)
        const isHead = m.uid === head.uid
        return (
          <div
            key={m.uid}
            className={[
              'absolute top-1/2 -translate-y-1/2 h-5 rounded-sm overflow-hidden',
              sp.unbounded
                ? 'bg-primary/25 border border-dashed border-primary/50'
                : (isHead ? PERIOD_CLASSES[periodState(m, now)].head : PERIOD_CLASSES[periodState(m, now)].member),
              sp.openEnd && !sp.unbounded ? 'rounded-r-none' : '',
              sp.openStart && !sp.unbounded ? 'rounded-l-none' : '',
            ].join(' ')}
            style={{ left: `${(sp.left * 100).toFixed(3)}%`, width: `${((sp.right - sp.left) * 100).toFixed(3)}%` }}
            data-testid={`timeline-bar-${m.uid}`}
            data-state={sp.unbounded ? 'unbounded' : periodState(m, now)}
            title={`${m.code || m.name || ''} ${m.start_date || '—'} → ${m.end_date || '—'}`}
          >
            {isHead && (
              <Link
                to="/$org/contracts/$contractUid"
                params={{ org, contractUid: head.uid }}
                className="absolute inset-0 px-1.5 flex items-center text-[11px] leading-none text-foreground hover:underline truncate"
                title={chainTitle}
                data-testid={`timeline-label-${head.uid}`}
              >
                <span className="truncate">{label}</span>
              </Link>
            )}
            {i === members.length - 1 && sp.openEnd && !sp.unbounded && (
              <span className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-r from-primary/80 to-transparent" aria-hidden="true" />
            )}
          </div>
        )
      })}

      {/* Today, carried through every lane so the axis label above reads as
          one continuous line. */}
      {todayInDomain && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-foreground/40 pointer-events-none"
          style={{ left: `${(frac(now) * 100).toFixed(3)}%` }}
          data-testid={`timeline-today-${head.uid}`}
          aria-hidden="true"
        />
      )}

      {/* Termination-date marker — the last day to denounce. */}
      {bandLeft !== null && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-destructive pointer-events-none"
          style={{ left: `${(bandLeft * 100).toFixed(3)}%` }}
          data-testid={`timeline-marker-${head.uid}`}
          title={t('timeline.termination_marker', { date: head.termination_date || '' })}
        />
      )}
    </div>
  )
}
